import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { ShieldCheck, BadgeCheck, QrCode, User as UserIcon, ArrowLeft } from "lucide-react";

// Sample crew members across associations + tiers — purely visual.
// Mirrors the production iCard component but renders multiple samples
// so the user can preview co-branded card variations side-by-side.
const SAMPLES = [
  { name: "Rajesh Kumar",   role: "spot_boy",    tier: "labor",     assoc: "cwa_mumbai", card_no: "CS-A12F38B0" },
  { name: "Meera Iyer",     role: "makeup",      tier: "labor",     assoc: "masec",      card_no: "CS-7B2C9D11" },
  { name: "Arjun Singh",    role: "sound",       tier: "labor",     assoc: "asmi",       card_no: "CS-44E810AC" },
  { name: "Priya Nair",     role: "ad",          tier: "direction", assoc: "ada",        card_no: "CS-E91F2D77" },
  { name: "Vikram Sharma",  role: "line_producer", tier: "direction", assoc: "iftpc",    card_no: "CS-12D3F4E5" },
  { name: "Anita Desai",    role: "art_dept",    tier: "direction", assoc: "ardai",      card_no: "CS-9A88B0C1" },
  { name: "Karthik Menon",  role: "dop",         tier: "creative",  assoc: "wica",       card_no: "CS-DD331AAB" },
  { name: "Sonia Kapoor",   role: "director",    tier: "creative",  assoc: "iftda",      card_no: "CS-FF6611A2" },
  { name: "Rahul Verma",    role: "main_talent", tier: "creative",  assoc: "cintaa",     card_no: "CS-009922DE" },
  // STAGE TIER — disco-light shimmer
  { name: "Neha Bhasin",      role: "choreographer",     tier: "stage", assoc: "fcai",  card_no: "CS-A1B2C3D4" },
  { name: "Disha Patel",      role: "background_dancer", tier: "stage", assoc: "aicda", card_no: "CS-E5F6A7B8" },
  // Music + writing
  { name: "Rohan Bhattacharya", role: "music_director",  tier: "creative", assoc: "mcai",  card_no: "CS-MUS00111" },
  { name: "Lata Krishnan",      role: "singer",          tier: "creative", assoc: "isra",  card_no: "CS-VOX00222" },
  { name: "Tanvir Ahmed",       role: "screenwriter",    tier: "creative", assoc: "swa",   card_no: "CS-PEN00333" },
];

const TIER_COLOR = {
  labor: "#4A4E51", direction: "#D9383A", creative: "#6B4EFF", stage: "#EC4899",
};

export default function ICardGallery() {
  const [assocs, setAssocs] = useState([]);

  useEffect(() => {
    api.get("/meta/associations").then(({ data }) => setAssocs(data));
  }, []);

  const byId = (id) => assocs.find((a) => a.id === id);

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10" data-testid="icard-gallery">
      <Link to="/icard" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-white/60 hover:text-white">
        <ArrowLeft size={14} /> Back to my iCard
      </Link>
      <div className="mt-4 text-[10px] tracking-[0.3em] uppercase text-white/50">Brand book · Co-Branded Samples</div>
      <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tight mt-2">Digital iCard Gallery</h1>
      <p className="mt-3 text-sm text-white/55 max-w-2xl">
        Same identity, three tiers, multiple associations. The association takes
        the lead at the top; <span className="text-white">CALL</span><span className="text-[#C0C0C0]">SHEET</span> signs the bottom.
      </p>

      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {SAMPLES.map((s) => {
          const a = byId(s.assoc);
          return <SampleCard key={s.card_no} sample={s} assoc={a} />;
        })}
      </div>
    </div>
  );
}

function SampleCard({ sample, assoc }) {
  const tierColor = TIER_COLOR[sample.tier];
  const isStage = sample.tier === "stage";
  return (
    <div
      className={`${isStage ? "icard-stage" : "icard-holo"} cs-grain rounded-2xl p-6 relative`}
      style={{
        background: "linear-gradient(160deg, #0e0e10 0%, #050505 60%, #0a0a0a 100%)",
        border: "1px solid rgba(255,255,255,0.12)",
        boxShadow: isStage
          ? `0 20px 60px -20px ${tierColor}AA, inset 0 1px 0 rgba(255,255,255,0.08)`
          : "0 20px 60px -25px rgba(0,0,0,0.9), inset 0 1px 0 rgba(255,255,255,0.05)",
        "--association-theme": tierColor,
      }}
      data-testid={`sample-${sample.card_no}`}
    >
      <div className="shimmer absolute inset-x-0 top-0 h-px" />

      {/* TOP: Association lead */}
      <div className="flex items-center gap-3">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center font-display font-bold text-[11px] text-white"
          style={{ background: assoc?.color || "#333", boxShadow: `0 0 0 1.5px ${assoc?.color}50, 0 8px 22px -8px ${assoc?.color}90` }}
        >
          {assoc?.name?.slice(0, 2) || "—"}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-base uppercase tracking-tight truncate" style={{ color: assoc?.color }}>
            {assoc?.name || "—"}
          </div>
          <div className="text-[9px] tracking-[0.22em] uppercase text-white/55 truncate">
            {assoc?.full || ""}
          </div>
        </div>
        <ShieldCheck size={14} className="text-[#F5B82E]" />
      </div>

      <div className="my-5 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />

      {/* MIDDLE: Identity */}
      <div className="grid grid-cols-[auto_1fr] gap-4 items-center">
        <div className="relative">
          <div className="w-16 h-16 rounded-xl bg-white/5 border border-white/15 flex items-center justify-center">
            <UserIcon size={28} className="text-white/40" />
          </div>
          <div className="absolute -bottom-1.5 -right-1.5 w-5 h-5 rounded-full flex items-center justify-center"
               style={{ background: tierColor }}>
            <BadgeCheck size={10} className="text-black" />
          </div>
        </div>
        <div className="min-w-0">
          <div className="text-[9px] tracking-[0.3em] uppercase text-white/45">Name</div>
          <div className="font-display text-lg uppercase tracking-tight truncate">{sample.name}</div>
          <div className="mt-2 flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: tierColor, boxShadow: `0 0 12px ${tierColor}` }} />
            <span className="text-[10px] uppercase tracking-[0.2em] text-white/70">{sample.role.replace("_", " ")}</span>
          </div>
        </div>
      </div>

      <div className="mt-5 flex items-end justify-between gap-4">
        <div>
          <div className="text-[9px] tracking-[0.3em] uppercase text-white/45">Card No.</div>
          <div className="text-xs font-mono mt-0.5">{sample.card_no}</div>
        </div>
        <div className="w-14 h-14 rounded border border-white/15 flex items-center justify-center bg-black/40">
          <QrCode size={42} className="text-white/70" />
        </div>
      </div>

      {/* BOTTOM: Powered by stamp */}
      <div className="mt-5 pt-4 border-t border-white/10 flex items-center justify-between">
        <div className="text-[9px] tracking-[0.3em] uppercase text-white/35">
          {sample.tier.toUpperCase()} TIER
        </div>
        <div className="text-[9px] tracking-[0.32em] uppercase text-white/55">
          Powered by{" "}
          <span className="text-white font-display font-bold">CALL</span>
          <span className="text-[#C0C0C0] font-display font-bold">SHEET</span>
        </div>
      </div>
    </div>
  );
}
